import { forwardRef, type MouseEvent } from "react"
import type { Keys } from "react-hotkeys-hook"

import type { ThemeKeys } from "../../theme"
import { Button } from "../Button"
import { Icon } from "../Icon/Icon"
import type { IconName } from "../Icon/iconNames"

export interface IconButtonProps {
  color?: Color
  hasPadding?: boolean
  hiddenLabel?: string
  icon: IconName | JSX.Element
  loading?: boolean
  hotkeys?: Keys
  onClick?: (event: MouseEvent | KeyboardEvent) => void
  onMouseDown?: (event: MouseEvent) => void
  onMouseUp?: (event: MouseEvent) => void
  radius?: "full" | "medium"
  size?: "small" | "medium" | "large"
  variant?: "solid" | "ghost"
  disabled?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(props, ref) {
    const {
      color = "grey",
      hasPadding = true,
      icon,
      loading = false,
      radius = "medium",
      size = "medium",
      variant = "ghost",
      disabled = false,
      onClick,
      onMouseDown,
      onMouseUp,
      hiddenLabel,
      ...implicitProps
    } = props

    const isDisabled = disabled || loading

    const processedIcon =
      typeof icon === "string" ? (
        <Icon
          name={icon}
          size={size}
          color={variant === "ghost" ? colorMap[color] : solidColorMap[color]}
        />
      ) : (
        icon
      )

    return (
      <Button
        {...implicitProps}
        ref={ref}
        renderStartIcon={() => processedIcon}
        color={color}
        disabled={isDisabled}
        loading={loading}
        size={size}
        invisible={variant === "ghost"}
        sx={{
          ...(radius === "full" ? { borderRadius: "50%" } : {})
        }}
        hasPadding={hasPadding}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        hiddenLabel={hiddenLabel}
      />
    )
  }
)

type Color = "grey" | "green" | "amber" | "purple" | "tomato"

type ColorMap = Record<Color, ThemeKeys<"color">>

const colorMap: ColorMap = {
  grey: "grey11",
  green: "green11",
  amber: "amber11",
  purple: "purple11",
  tomato: "tomato11"
}

const solidColorMap: ColorMap = {
  grey: "grey11",
  green: "grey2",
  amber: "grey2",
  purple: "grey2",
  tomato: "grey2"
}
